import { TaskHierarchyBackgroundContext } from '../TaskListView/TaskHierarchy'
import React, { Component } from 'react'
import { Animated, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import styles, { colors } from '../styles/global'
import store from '../../redux/store'
import MemberTag from '../Tags/MemberTag'
import TaskSummarizeTags from '../Tags/TaskSummarizeTags'
import SVGGenericUser from '../../assets/svg/SVGGenericUser'
import ProjectHelper from '../SettingsView/ProjectsSettings/ProjectHelper'
import ContactsHelper, { PHOTO_SIZE_50 } from './Utils/ContactsHelper'
import ContactPhoto from './ContactPhoto'
import Backend from '../../utils/BackendBridge'
import BacklinksTag from '../Tags/BacklinksTag'
import ContactCommentsWrapper from '../Tags/ContactCommentsWrapper'
import moment from 'moment'
import PrivacyTag from '../Tags/PrivacyTag'
import { FEED_CONTACT_OBJECT_TYPE, FEED_USER_OBJECT_TYPE } from '../Feeds/Utils/FeedsConstants'
import SocialText from '../UIControls/SocialText/SocialText'
import Icon from '../Icon'
import Swipeable from 'react-native-gesture-handler/Swipeable'
import { createSwipeCloseGuard } from '../../hooks/useSwipeCloseGuard'
import NavigationService from '../../utils/NavigationService'
import { getDateFormat, getTimeFormat } from '../UIComponents/FloatModals/DateFormatPickerModal'
import SwipeNewTaskWrapper from '../NotesView/SwipeNewTaskWrapper'
import { LINKED_OBJECT_TYPE_CONTACT } from '../../utils/LinkingHelper'
import ObjectNoteTag from '../Tags/ObjectNoteTag'
import ContactStatusTag from '../Tags/ContactStatusTag'
import { translate } from '../../i18n/TranslationService'
import { setSelectedNavItem } from '../../redux/actions'
import { DV_TAB_CONTACT_PROPERTIES, DV_TAB_USER_PROFILE } from '../../utils/TabNavigationConstants'
import v4 from 'uuid/v4'
import {
    getContactBacklinksWatcherKey,
    getContactInfoPreview,
    getContactItemStoreUpdate,
    getContactPresentationData,
} from './contactItemStoreUpdate'
import ProjectMoveIndicator from '../UIComponents/ProjectMoveIndicator'

export const CONTACT_BACKLINKS_ROOT_MARGIN = '600px 0px'

export default class ContactItem extends Component {
    static contextType = TaskHierarchyBackgroundContext

    constructor(props) {
        super(props)
        const storeState = store.getState()

        this.state = {
            loggedUserProjects: storeState.loggedUserProjects,
            smallScreenNavigation: storeState.smallScreenNavigation,
            isMiddleScreen: storeState.isMiddleScreen,
            loggedUserId: storeState.loggedUser.uid,
            backlinksTasksCount: 0,
            backlinkTaskObject: null,
            backlinksNotesCount: 0,
            backlinkNoteObject: null,
            blockOpen: false,
            showNewTaskPopup: false,
            panColor: new Animated.Value(0),
            unsubscribe: store.subscribe(this.updateState),
            showTagsSummarizeArea: false,
        }

        this.itemSwipe = React.createRef()
        this.containerRef = React.createRef()
        // AT-2449 — Swipeable can deliver its close callbacks the wrong way round
        // and leave the row permanently unopenable. See the hook module.
        this.swipeCloseGuard = createSwipeCloseGuard(blockOpen => this.setState({ blockOpen }))
    }

    componentDidMount() {
        const target = this.containerRef.current
        if (typeof IntersectionObserver === 'undefined' || !target) {
            this.startBacklinksWatcher()
            return
        }

        try {
            this.backlinksObserver = new IntersectionObserver(
                entries => {
                    if (!entries.some(entry => entry.isIntersecting)) return
                    this.backlinksObserver?.disconnect()
                    this.backlinksObserver = null
                    this.startBacklinksWatcher()
                },
                { rootMargin: CONTACT_BACKLINKS_ROOT_MARGIN }
            )
            this.backlinksObserver.observe(target)
        } catch {
            this.backlinksObserver?.disconnect()
            this.backlinksObserver = null
            this.startBacklinksWatcher()
        }
    }

    startBacklinksWatcher = () => {
        if (this.backlinksWatcherKey) return
        const { loggedUserProjects } = this.state
        const { projectIndex, contact } = this.props
        const projectId = loggedUserProjects[projectIndex].id
        this.backlinksWatcherKey = getContactBacklinksWatcherKey(projectId, contact.uid, v4())

        Backend.watchBacklinksCount(
            projectId,
            {
                type: LINKED_OBJECT_TYPE_CONTACT,
                idsField: 'linkedParentContactsIds',
                id: contact.uid,
            },
            (parentObjectType, parentsAmount, aloneParentObject) => {
                if (parentObjectType === 'tasks') {
                    this.setState({ backlinksTasksCount: parentsAmount, backlinkTaskObject: aloneParentObject })
                } else if (parentObjectType === 'notes') {
                    this.setState({ backlinksNotesCount: parentsAmount, backlinkNoteObject: aloneParentObject })
                }
            },
            this.backlinksWatcherKey
        )
    }

    componentWillUnmount() {
        this.backlinksObserver?.disconnect()
        const { contact } = this.props
        if (this.backlinksWatcherKey) Backend.unwatchBacklinksCount(contact.uid, this.backlinksWatcherKey)
        this.state.unsubscribe()
    }

    updateState = () => {
        const storeState = store.getState()

        this.setState(state => getContactItemStoreUpdate(state, storeState))
    }

    toggleShowTagsSummarizeArea = e => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        this.setState(state => ({ showTagsSummarizeArea: !state.showTagsSummarizeArea }))
    }

    // Swipeable calls the swipe renderers during its own render, so setting state synchronously
    // would update this component while another one renders. dragX is stable per Swipeable
    // instance, so deferring the swap adopts it just as reliably, one render later.
    adoptPanColor = dragX => {
        if (this.state.panColor !== dragX) {
            Promise.resolve().then(() => this.setState({ panColor: dragX }))
        }
    }

    renderLeftSwipe = (progress, dragX) => {
        this.adoptPanColor(dragX)

        return <View style={{ width: 150 }} />
    }

    onLeftSwipe = () => {
        const { projectIndex, contact, isMember } = this.props
        const { loggedUserProjects } = this.state
        this.itemSwipe.current.close()

        NavigationService.navigate(isMember ? 'UserDetailedView' : 'ContactDetailedView', {
            contact: contact,
            project: loggedUserProjects[projectIndex],
        })
        store.dispatch(setSelectedNavItem(isMember ? DV_TAB_USER_PROFILE : DV_TAB_CONTACT_PROPERTIES))
    }

    renderRightSwipe = (progress, dragX) => {
        this.adoptPanColor(dragX)

        return <View style={{ width: 150 }} />
    }

    onRightSwipe = () => {
        this.itemSwipe.current.close()
        setTimeout(() => this.setState({ showNewTaskPopup: true }))
    }

    cancelPopover = () => {
        this.setState({ showNewTaskPopup: false })
    }

    onOpenDV = () => {
        const { loggedUserProjects } = this.state
        const { projectIndex, contact, isMember } = this.props
        const project = loggedUserProjects[projectIndex]
        NavigationService.navigate(isMember ? 'UserDetailedView' : 'ContactDetailedView', {
            contact: contact,
            project: project,
        })
        store.dispatch(setSelectedNavItem(isMember ? DV_TAB_USER_PROFILE : DV_TAB_CONTACT_PROPERTIES))
    }

    render() {
        const {
            loggedUserProjects,
            blockOpen,
            panColor,
            showNewTaskPopup,
            backlinksTasksCount,
            backlinkTaskObject,
            backlinksNotesCount,
            backlinkNoteObject,
            loggedUserId,
        } = this.state
        const { projectIndex, contact: contactFromProps, isMember, inCommentPopup, onPress } = this.props
        const projectPrivacy = isMember ? ProjectHelper.getUserPrivacyInProject(projectIndex, contactFromProps) : null
        const contact = getContactPresentationData(contactFromProps, projectPrivacy)

        const projectId = loggedUserProjects[projectIndex].id
        const showContact = isMember || !ContactsHelper.isPrivateContact(contact)
        const contactHighlightColor = ProjectHelper.getUserHighlightInProject(projectIndex, contact)

        const restingBackgroundColor = inCommentPopup ? colors.Secondary200 : this.context
        const outputColors = [colors.UtilityYellow125, restingBackgroundColor, colors.UtilityGreen125]
        const backColor = panColor.interpolate({
            inputRange: [-100, 0, 100],
            outputRange: outputColors,
            extrapolate: 'clamp',
        })

        const backColorHighlight = panColor.interpolate({
            inputRange: [-100, 0, 100],
            outputRange: [colors.UtilityYellow125, contactHighlightColor, colors.UtilityGreen125],
            extrapolate: 'clamp',
        })

        const contactPhotoURL50 = ContactsHelper.getContactPhotoURL(contact, isMember, PHOTO_SIZE_50)
        const role = ProjectHelper.getUserRoleInProject(projectId, contact.uid, contact.role)
        const company = ProjectHelper.getUserCompanyInProject(projectId, contact.uid, contact.company)
        const description = ProjectHelper.getUserDescriptionInProject(
            projectId,
            contact.uid,
            contact.description,
            contact.extendedDescription,
            true
        )
        const highlightColor =
            inCommentPopup || contactHighlightColor.toLowerCase() === '#ffffff' ? backColor : backColorHighlight
        const usesCommentPopupBackground = inCommentPopup

        const parseDate = date => {
            if (Date.now() - date < 60000) {
                return translate('Just now')
            }
            return `${translate('Edited')}: ${moment(date).format(`${getTimeFormat(true)} of ${getDateFormat()}`)}`
        }

        const userInfo = getContactInfoPreview(
            !role && !company && !description
                ? ''
                : `${role ? role : ''}${role && (company || description) ? ' • ' : ''}${company ? company : ''}${
                      company && description ? ' • ' : ''
                  }${description ? description : ''}`
        )

        const backlinksCount = backlinksTasksCount + backlinksNotesCount
        const backlinkObject =
            backlinksCount === 1 ? (backlinksTasksCount === 1 ? backlinkTaskObject : backlinkNoteObject) : null

        const userIsLoggedUser = loggedUserId === contact.uid
        const loggedUserIsCreator = loggedUserId === contact.recorderUserId
        const loggedUserCanUpdateObject = isMember
            ? userIsLoggedUser || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)
            : loggedUserIsCreator || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)

        const commentsData = isMember ? contact.commentsData[projectId] : contact.commentsData

        const amountTags =
            (isMember || (!isMember && contact.contactStatusId) ? 1 : 0) +
            (contact.noteId || (contact.noteIdsByProject && contact.noteIdsByProject[projectId]) ? 1 : 0) +
            (backlinksCount > 0 ? 1 : 0) +
            (contact.isPrivate ? 1 : 0) +
            (commentsData && !inCommentPopup ? 1 : 0)

        const showSummarizeTag = (this.state.smallScreenNavigation && amountTags > 1) || amountTags > 2

        return showContact ? (
            <View ref={this.containerRef}>
                <View style={localStyles.swipeContainer}>
                    <View style={localStyles.leftSwipeArea}>
                        <Icon name="circle-details" size={18} color={colors.UtilityGreen200} />
                        <View style={{ marginLeft: 4 }}>
                            <Text style={[styles.subtitle2, { color: colors.UtilityGreen200 }]}>
                                {translate('Details')}
                            </Text>
                        </View>
                    </View>

                    <View style={localStyles.rightSwipeArea}>
                        <View style={localStyles.rightSwipeAreaContainer}>
                            <Icon name={'check-square'} size={18} color={colors.UtilityYellow200} />
                            <View style={{ marginLeft: 4 }}>
                                <Text style={[styles.subtitle2, { color: colors.UtilityYellow200 }]}>
                                    {translate('Add task')}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                <Swipeable
                    useNativeAnimations={false}
                    ref={this.itemSwipe}
                    rightThreshold={80}
                    leftThreshold={80}
                    enabled={!inCommentPopup}
                    renderLeftActions={this.renderLeftSwipe}
                    onSwipeableLeftWillOpen={this.onLeftSwipe}
                    renderRightActions={this.renderRightSwipe}
                    onSwipeableRightWillOpen={this.onRightSwipe}
                    overshootLeft={false}
                    overshootRight={false}
                    friction={2}
                    containerStyle={{ overflow: 'visible' }}
                    failOffsetY={[-5, 5]}
                    {...this.swipeCloseGuard}
                >
                    <TouchableOpacity
                        onPress={blockOpen ? undefined : inCommentPopup ? onPress : this.onOpenDV}
                        disabled={blockOpen}
                        activeOpacity={blockOpen ? 1 : 0.5}
                    >
                        <Animated.View
                            style={[
                                localStyles.container,
                                {
                                    backgroundColor: highlightColor,
                                    height: this.state.showTagsSummarizeArea ? 122 : 90,
                                },
                            ]}
                        >
                            <ProjectMoveIndicator object={contact} projectId={projectId} />
                            <View style={localStyles.mainRow}>
                                <View style={localStyles.avatarContainer}>
                                    {contact.photoURL != null && contact.photoURL !== '' ? (
                                        <ContactPhoto uri={contactPhotoURL50} style={localStyles.image} />
                                    ) : (
                                        <SVGGenericUser
                                            width={48}
                                            height={48}
                                            svgid={`ci_p_${contact.uid}_${projectIndex}`}
                                        />
                                    )}
                                </View>

                                <View style={localStyles.userData}>
                                    <SocialText
                                        showEllipsis
                                        style={[
                                            localStyles.name,
                                            usesCommentPopupBackground && localStyles.textInCommentPopup,
                                        ]}
                                        numberOfLines={1}
                                        bgColor={
                                            contactHighlightColor.toLowerCase() !== '#ffffff'
                                                ? backColorHighlight
                                                : backColor
                                        }
                                        projectId={projectId}
                                    >
                                        {contact.displayName}
                                    </SocialText>

                                    {!!userInfo && (
                                        <Text
                                            style={[
                                                localStyles.description,
                                                usesCommentPopupBackground && localStyles.secondaryTextInCommentPopup,
                                            ]}
                                            numberOfLines={1}
                                        >
                                            {userInfo}
                                        </Text>
                                    )}

                                    <Text
                                        style={[
                                            styles.caption2,
                                            localStyles.updatedInfo,
                                            usesCommentPopupBackground && localStyles.secondaryTextInCommentPopup,
                                        ]}
                                        numberOfLines={1}
                                    >
                                        {parseDate(contact.lastEditionDate)}
                                    </Text>
                                </View>

                                <View style={localStyles.buttonSection}>
                                    {showSummarizeTag ? (
                                        <TaskSummarizeTags
                                            amountTags={amountTags}
                                            onPress={this.toggleShowTagsSummarizeArea}
                                        />
                                    ) : (
                                        <>
                                            {!isMember && contact.contactStatusId && (
                                                <ContactStatusTag
                                                    projectId={projectId}
                                                    contactStatusId={contact.contactStatusId}
                                                    contact={contact}
                                                />
                                            )}
                                            {isMember && <MemberTag />}
                                            {(contact.noteId ||
                                                (contact.noteIdsByProject && contact.noteIdsByProject[projectId])) && (
                                                <ObjectNoteTag
                                                    objectId={contact.uid}
                                                    objectType="contacts"
                                                    projectId={projectId}
                                                    style={{ marginLeft: 8 }}
                                                />
                                            )}
                                            {backlinksCount > 0 && (
                                                <BacklinksTag
                                                    object={contact}
                                                    objectType={LINKED_OBJECT_TYPE_CONTACT}
                                                    projectId={projectId}
                                                    style={{ marginLeft: 8 }}
                                                    backlinksCount={backlinksCount}
                                                    backlinkObject={backlinkObject}
                                                />
                                            )}

                                            {contact.isPrivate && (
                                                <PrivacyTag
                                                    projectId={projectId}
                                                    object={contact}
                                                    objectType={
                                                        isMember ? FEED_USER_OBJECT_TYPE : FEED_CONTACT_OBJECT_TYPE
                                                    }
                                                    style={{ marginLeft: 8 }}
                                                    disabled={!loggedUserCanUpdateObject}
                                                />
                                            )}

                                            {!!commentsData && !inCommentPopup && (
                                                <ContactCommentsWrapper
                                                    commentsData={commentsData}
                                                    projectId={projectId}
                                                    contact={contact}
                                                    isMember={isMember}
                                                />
                                            )}
                                        </>
                                    )}
                                </View>
                            </View>

                            {showSummarizeTag && this.state.showTagsSummarizeArea && (
                                <View style={localStyles.expandedTagsContainer}>
                                    {!isMember && contact.contactStatusId && (
                                        <ContactStatusTag
                                            projectId={projectId}
                                            contactStatusId={contact.contactStatusId}
                                            contact={contact}
                                            style={{ marginLeft: 8 }}
                                        />
                                    )}
                                    {isMember && <MemberTag style={{ marginLeft: 8 }} />}
                                    {(contact.noteId ||
                                        (contact.noteIdsByProject && contact.noteIdsByProject[projectId])) && (
                                        <ObjectNoteTag
                                            objectId={contact.uid}
                                            objectType="contacts"
                                            projectId={projectId}
                                            style={{ marginLeft: 8 }}
                                        />
                                    )}
                                    {backlinksCount > 0 && (
                                        <BacklinksTag
                                            object={contact}
                                            objectType={LINKED_OBJECT_TYPE_CONTACT}
                                            projectId={projectId}
                                            style={{ marginLeft: 8 }}
                                            backlinksCount={backlinksCount}
                                            backlinkObject={backlinkObject}
                                        />
                                    )}

                                    {contact.isPrivate && (
                                        <PrivacyTag
                                            projectId={projectId}
                                            object={contact}
                                            objectType={isMember ? FEED_USER_OBJECT_TYPE : FEED_CONTACT_OBJECT_TYPE}
                                            style={{ marginLeft: 8 }}
                                            disabled={!loggedUserCanUpdateObject}
                                        />
                                    )}

                                    {!!commentsData && !inCommentPopup && (
                                        <ContactCommentsWrapper
                                            commentsData={commentsData}
                                            projectId={projectId}
                                            contact={contact}
                                            isMember={isMember}
                                            style={{ marginLeft: 8 }}
                                        />
                                    )}
                                </View>
                            )}
                        </Animated.View>
                    </TouchableOpacity>
                </Swipeable>

                <SwipeNewTaskWrapper
                    projectId={projectId}
                    objectId={contact.uid}
                    sourceType={isMember ? FEED_USER_OBJECT_TYPE : FEED_CONTACT_OBJECT_TYPE}
                    showPopup={showNewTaskPopup}
                    cancelPopover={this.cancelPopover}
                />
            </View>
        ) : null
    }
}

const localStyles = StyleSheet.create({
    container: {
        paddingTop: 8,
        paddingBottom: 10,
        marginLeft: -8,
        marginRight: -8,
        paddingLeft: 8,
        paddingRight: 8,
        borderRadius: 4,
        flexDirection: 'column',
        overflow: 'hidden',
    },
    mainRow: {
        flexDirection: 'row',
        width: '100%',
        height: 90,
    },
    expandedTagsContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingRight: 8,
        paddingBottom: 8,
        marginTop: 4,
    },
    avatarContainer: {
        justifyContent: 'flex-start',
        overflow: 'hidden',
        width: 48,
        height: 48,
        borderRadius: 100,
    },
    image: {
        width: 48,
        height: 48,
        borderRadius: 100,
    },
    userData: {
        flex: 1,
        alignSelf: 'flex-start',
        marginLeft: 8,
    },
    name: {
        ...styles.body1,
        color: colors.Text01,
    },
    description: {
        ...styles.body2,
        color: colors.Text02,
    },
    updatedInfo: {
        color: colors.Text03,
    },
    textInCommentPopup: {
        color: colors.UtilityBlue100,
    },
    secondaryTextInCommentPopup: {
        color: colors.UtilityBlue125,
    },
    buttonSection: {
        position: 'absolute',
        top: 8,
        right: 8,
        marginLeft: 8,
        flexDirection: 'row',
    },
    swipeContainer: {
        height: '100%',
        width: '100%',
        borderRadius: 4,
        position: 'absolute',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    leftSwipeArea: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '50%',
        height: '100%',
        backgroundColor: colors.UtilityGreen100,
        borderRadius: 4,
        paddingLeft: 12,
    },
    rightSwipeAreaContainer: {
        marginLeft: 'auto',
        flexDirection: 'row',
        alignItems: 'center',
    },
    rightSwipeArea: {
        flexDirection: 'row',
        width: '50%',
        height: '100%',
        backgroundColor: colors.UtilityYellow100,
        borderRadius: 4,
        paddingRight: 12,
    },
})
